import { useState, useEffect, useMemo, useCallback } from 'preact/hooks';
import clsx from 'clsx';
import { useMediaQuery, useQueryLocation, useAuth, useWorkoutImage } from '../hooks';
import { parseHabitTrackerData, getHabitTrackerData } from '../lib/habitTrackerHelpers';
import useStorefrontAPI from '../hooks/use-storefront-api';
import { STOREFRONT_QUERIES, transformVideos } from '../lib/storefrontClient';
import { filterAndSortVideos } from '../utils/video-filters';
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../calendar.css';
import { formatDateKey, formatDisplayDate } from '../utils/date';
import StreakStats from '../components/StreakStats';
import Link from '../components/Link';
import WorkoutImage from '../components/WorkoutImage';
import Header from '../components/Header';


function Calendar() {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isTabletPortrait = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
    const [location] = useQueryLocation();
    const [showSuccessBanner, setShowSuccessBanner] = useState(false);
    const [completedDateMessage, setCompletedDateMessage] = useState('');
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date()); // Current month - moved up before useEffect
    const [workoutDays, setWorkoutDays] = useState({});
    
    // Consolidated class names
    const oswaldHeadingClasses = isMobile
        ? "tw-text-black tw-font-['Poppins'] tw-text-[1.25em] tw-font-semibold tw-leading-[110%] tw-uppercase tw-m-0"
        : "tw-text-black tw-font-['Anton'] tw-text-[32px] tw-font-normal tw-leading-[110%] tw-uppercase tw-m-0";
    const responsivePaddingClasses = (isMobile || isTabletPortrait) ? 'tw-px-2.5' : 'tw-px-5';
    const cardContainerClasses = 'tw-max-w-full tw-h-auto tw-rounded-[20px] tw-bg-[#F7F8F8] tw-w-full';
    const statsContainerMobileClasses = 'tw-flex tw-w-full tw-max-w-[350px] tw-px-4 tw-py-7 tw-flex-col tw-items-stretch tw-gap-4 tw-rounded-xl tw-bg-white';
    const workoutImageClasses = 'tw-object-cover tw-rounded-lg';
    const ctaHeadingClasses = clsx(oswaldHeadingClasses, 'tw-mb-4 tw-break-words tw-whitespace-normal');
    const badgesLinkClasses = "tw-text-[#171717] tw-text-center tw-font-['Poppins'] tw-text-[14px] tw-font-semibold tw-leading-normal tw-tracking-[0.28px] tw-underline tw-uppercase";
    const flexColCenterClasses = 'tw-flex tw-flex-col tw-items-center';
    const flexColStartClasses = 'tw-flex tw-flex-col tw-items-start';
    const calendarCardClasses = 'tw-flex tw-flex-col tw-gap-4 tw-self-stretch tw-max-w-full tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-p-6 tw-w-full';
    const mobileWorkoutContainerClasses = 'tw-flex tw-justify-center tw-w-full tw-px-4';
    const mobileWorkoutInnerClasses = 'tw-flex tw-flex-col tw-items-start tw-w-full';

    const { isAuthenticated, logout, isLoading: authLoading, hasActiveSubscription, subscriptionLoading } = useAuth();
    
    // Get workout image from shared hook
    const { thumbnailUrl } = useWorkoutImage();
    
    // Fetch habit tracker data from Liquid template
    const [habitTrackerData, setHabitTrackerData] = useState({ 
        currentStreak: 0, 
        personalRecord: 0, 
        personalRecordDate: null, 
        workoutDays: {}, 
        badges: [] 
    });
    const [habitTrackerLoading, setHabitTrackerLoading] = useState(true);

    // Check for completed workout date from sessionStorage (if navigating from completion)
    useEffect(() => {
        const completedDate = sessionStorage.getItem('completedWorkoutDate');
        
        if (completedDate) {
            // Clear the flag immediately
            sessionStorage.removeItem('completedWorkoutDate');
            
            const dateObj = new Date(completedDate + 'T00:00:00');
            const formattedDate = formatDisplayDate(completedDate);
            setCompletedDateMessage(`Workout completed for ${formattedDate}! ⚡️`);
            setShowSuccessBanner(true);
            
            // Navigate calendar to the month of the completed workout
            setCurrentDate(dateObj);
            
            // Auto-hide after 5 seconds
            setTimeout(() => {
                setShowSuccessBanner(false);
            }, 5000);
        }
    }, []);
    
    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            getHabitTrackerData()
                .then(data => {
                    console.log('📥 Loaded habit tracker data from backend:', {
                        workoutDaysCount: Object.keys(data.workoutDays || {}).length,
                        currentStreak: data.currentStreak,
                        personalRecord: data.personalRecord
                    });
                    
                    setHabitTrackerData(data);
                    setHabitTrackerLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load habit tracker data:', err);
                    setHabitTrackerLoading(false);
                });
        }
    }, [isAuthenticated, authLoading, location]); // Refetch when location changes
    
    // Show loading/redirect state
    if (!isAuthenticated || subscriptionLoading) {
        return (
            <div className="tw-w-full tw-p-5 tw-text-center">
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[400px]">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-gray-900 tw-mb-4"></div>
                    <p>{!isAuthenticated ? 'Logging out...' : 'Loading...'}</p>
                </div>
            </div>
        );
    }

    // Check for active subscription
    if (!hasActiveSubscription) {
        return (
            <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50">
                <div className="tw-max-w-md tw-w-full tw-mx-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8 tw-text-center">
                        <div className="tw-mb-6">
                            <div className="tw-text-6xl tw-mb-4">🔒</div>
                            <h1 className="tw-text-3xl tw-font-bold tw-mb-2">Subscription Required</h1>
                            <p className="tw-text-gray-600">You need an active subscription to access the calendar and track your workouts.</p>
                        </div>
                        
                        <a 
                            href="https://lexijwellness.com/products/ljw-guided-workouts?variant=45633267237060&selling_plan=3552968900"
                            className="tw-inline-block tw-w-full tw-bg-black tw-text-white tw-py-3 tw-px-6 tw-rounded-lg tw-font-semibold tw-hover:bg-gray-800 tw-transition-colors"
                        >
                            Get Subscription
                        </a>
                    </div>
                </div>
            </div>
        );
    }
    
    // Show loading state
    if (habitTrackerLoading) {
        return (
            <div className="tw-w-full tw-p-5 tw-text-center">
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[400px]">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-gray-900 tw-mb-4"></div>
                    <p>Loading calendar...</p>
                </div>
            </div>
        );
    }

    // Memoize streak data
    const currentStreak = habitTrackerData.currentStreak;
    const personalRecord = habitTrackerData.personalRecord;
    const apiWorkoutDays = habitTrackerData.workoutDays;

    const isActive = useMemo(() => (page) => {
        if (page === 'calendar') return location === '/calendar';
        if (page === 'badges') return location === '/badges';
        if (page === 'videos') return location === '/';
        return false;
    }, [location]);
    
    const handleLogoutClick = useCallback(() => {
        setShowLogoutConfirm(true);
    }, []);

    const handleLogoutConfirm = useCallback(() => {
        setShowLogoutConfirm(false);
        logout();
    }, [logout]);

    const handleLogoutCancel = useCallback(() => {
        setShowLogoutConfirm(false);
    }, []);

    const navLinks = useMemo(() => [
        { href: '/', label: 'Videos', page: 'videos', roundedClass: 'tw-rounded-l-lg', internal: true },
        { href: '/badges', label: 'Badges', page: 'badges', internal: true },
        { href: '/calendar', label: 'Calendar', page: 'calendar', internal: true },
        { href: 'https://www.facebook.com/share/g/1ADj3sTSs8/', label: 'Facebook Group', page: null, internal: false },
        { href: '/account', label: 'Account', page: null, internal: false, onClick: () => { sessionStorage.removeItem('was_on_habit_tracker'); window.location.href = '/account'; } },
        { label: 'Logout', page: null, roundedClass: 'tw-rounded-r-lg', internal: false, onClick: handleLogoutClick }
    ], [handleLogoutClick]);
    
    // Convert API workout days to calendar format using useMemo
    
    const formattedWorkoutDays = useMemo(() => {
        // Only process if we have API data loaded
        if (!apiWorkoutDays || Object.keys(apiWorkoutDays).length === 0) {
            return {};
        }
        
        const formattedDays = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset to start of day for comparison
        
        Object.keys(apiWorkoutDays).forEach(date => {
            const workout = apiWorkoutDays[date];
            // If workout exists for this date, mark as completed
            // New format: workout object exists = completed
            // Old format: workout.status === 'Completed'
            formattedDays[date] = workout ? true : false;
        });
        
        // Mark past days without workouts as missed (false)
        // Check all days in the current month view
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentYear, currentMonth, day);
            date.setHours(0, 0, 0, 0);
            const dateKey = formatDateKey(date);
            
            // If date is in the past and no workout data exists, mark as missed
            if (date < today && formattedDays[dateKey] === undefined) {
                formattedDays[dateKey] = false; // Missed
            }
        }
        
        return formattedDays;
    }, [apiWorkoutDays, currentDate]);
    
    // Update workoutDays when formattedWorkoutDays changes
    useEffect(() => {
        if (Object.keys(formattedWorkoutDays).length > 0) {
            setWorkoutDays(formattedWorkoutDays);
        }
    }, [formattedWorkoutDays]);

    // Memoized handlers
    const handleDayClick = useCallback((date) => {
        const dateKey = formatDateKey(date);
        const currentStatus = workoutDays[dateKey];
        
        setWorkoutDays(prev => ({
            ...prev,
            [dateKey]: currentStatus === true ? false : currentStatus === false ? undefined : true
        }));
    }, [workoutDays]);

    const handleActiveStartDateChange = useCallback(({ activeStartDate }) => {
        setCurrentDate(activeStartDate);
    }, []);

    const handleCloseBanner = useCallback(() => {
        setShowSuccessBanner(false);
    }, []);

    // Custom tile content for each day
    // Corner icons - commented out for now
    // const tileContent = ({ date, view }) => {
    //     if (view !== 'month') return null;
    //     
    //     const dateKey = formatDateKey(date);
    //     const status = workoutDays[dateKey];
    //     
    //     if (status === true) {
    //         return (
    //             <div className="tw-absolute tw-top-1 tw-right-1 tw-w-3 tw-h-3 tw-bg-[#00A7D3] tw-rounded tw-flex tw-items-center tw-justify-center tw-text-white tw-text-xs tw-font-bold">
    //                 ✓
    //             </div>
    //         );
    //     } else if (status === false) {
    //         return (
    //             <div className="tw-absolute tw-top-1 tw-right-1 tw-w-3 tw-h-3 tw-bg-red-500 tw-rounded tw-flex tw-items-center tw-justify-center tw-text-white tw-text-xs tw-font-bold">
    //                 ✗
    //             </div>
    //         );
    //     }
    //     
    //     return null;
    // };
    const tileContent = useCallback(() => null, []);

    // Get the start of the current week (Monday at 12:00 AM)
    const getStartOfWeek = useCallback((date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        const day = d.getDay();
        // If Sunday (0), go back 6 days to get to Monday
        // Otherwise, go back (day - 1) days
        const diff = day === 0 ? 6 : day - 1;
        d.setDate(d.getDate() - diff);
        return d;
    }, []);

    // Custom class names for tiles
    const tileClassName = useCallback(({ date, view }) => {
        if (view !== 'month') return '';
        
        const dateKey = formatDateKey(date);
        const status = workoutDays[dateKey];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tileDate = new Date(date);
        tileDate.setHours(0, 0, 0, 0);
        const isToday = date.toDateString() === today.toDateString();
        const isFuture = tileDate > today;
        const isSunday = date.getDay() === 0;
        
        // Week-based locking: check if date is before the current week's Monday
        const startOfCurrentWeek = getStartOfWeek(today);
        const isLockedWeek = tileDate < startOfCurrentWeek;
        
        // Only show workout status for dates in the current visible month
        const isCurrentMonth = date.getMonth() === currentDate.getMonth() && 
                               date.getFullYear() === currentDate.getFullYear();
        
        let classes = 'tw-relative tw-cursor-default tw-border-0';
        
        if (isMobile) {
            classes += ' tw-h-20 tw-w-full';
        } else if (isTabletPortrait) {
            classes += ' tw-h-[120px] tw-w-full';
        } else {
            classes += ' tw-h-[160px] tw-w-[171px]';
        }
        
        // Sundays get special styling (rest days)
        if (isSunday && isCurrentMonth) {
            classes += ' tw-text-white tw-cursor-not-allowed';
            return classes + ' sunday-rest-day';
        }
        
        if (isToday) {
            classes += ' tw-ring-2 tw-ring-[#5690b1]';
        }
        
        // Check if date is in a locked week (before current week's Monday)
        if (isLockedWeek && isCurrentMonth) {
            // Locked week - show completed or missed (red for missed non-Sunday days)
            if (status === true) {
                classes += ' completed-day tw-text-white tw-cursor-not-allowed';
            } else {
                // Missed day in locked week - show red
                classes += ' missed-day tw-text-white tw-cursor-not-allowed';
            }
        }
        // Check if date is in the future
        else if (isFuture && isCurrentMonth) {
            classes += ' tw-bg-gray-300 tw-text-gray-500 tw-opacity-60 tw-cursor-not-allowed';
        } else if (isCurrentMonth) {
            // Current week - editable
            if (status === true) {
                classes += ' completed-day tw-text-white';
            } else if (status === false) {
                classes += ' missed-day tw-text-white';
            } else {
                classes += ' tw-bg-transparent tw-text-[#171717] tw-opacity-50';
            }
        } else {
            // Dates from other months shown in the calendar view
            classes += ' tw-bg-transparent tw-text-[#171717] tw-opacity-30';
        }
        
        return classes;
    }, [workoutDays, currentDate, isMobile, isTabletPortrait, getStartOfWeek]);

    const tileDisabled = useCallback(({ date, view }) => {
        if (view !== 'month') return false;
        
        // Sundays are always disabled (rest days)
        if (date.getDay() === 0) return true;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tileDate = new Date(date);
        tileDate.setHours(0, 0, 0, 0);
        
        // Future dates are disabled
        if (tileDate > today) return true;
        
        // Week-based locking: dates before current week's Monday are disabled
        const startOfCurrentWeek = getStartOfWeek(today);
        if (tileDate < startOfCurrentWeek) return true;
        
        return false;
    }, []);

    const navigationLabel = useCallback(({ date }) => 
        date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    , []);

    return (
        <>
            {/* Header */}
            <Header currentStreak={currentStreak} />
            
            <div className="tw-w-full tw-flex tw-justify-center">
                {/* Success Banner */}
            {showSuccessBanner && (
                <div className="tw-fixed tw-top-8 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-[1000] tw-bg-green-500 tw-text-white tw-px-12 tw-py-6 tw-rounded-2xl tw-shadow-2xl tw-flex tw-items-center tw-gap-4 tw-max-w-[90%] tw-min-w-[400px]">
                    <span className="tw-text-2xl tw-font-['Poppins'] tw-font-semibold tw-normal-case">{completedDateMessage}</span>
                    <button 
                        onClick={handleCloseBanner}
                        className="tw-bg-transparent tw-border-0 tw-text-white tw-text-4xl tw-cursor-pointer tw-ml-4 hover:tw-opacity-80"
                    >
                        ×
                    </button>
                </div>
            )}
            
            <div className="tw-w-full tw-max-w-[1233px] tw-mx-auto">
                {/* Desktop Navigation Bar */}
                {!isMobile && (
                    <nav className="tw-flex tw-justify-center tw-items-center tw-w-full tw-my-14 tw-p-0" style={{display: 'flex'}}>
                        <div className="tw-flex tw-justify-center tw-items-center">
                            {navLinks.map((link, index) => 
                                link.internal ? (
                                    <Link
                                        key={index}
                                        href={link.href} 
                                        className={clsx(
                                            "nav-link",
                                            link.roundedClass,
                                            link.page && isActive(link.page) ? "nav-link-active" : "nav-link-inactive"
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                ) : link.onClick ? (
                                    <button
                                        key={index}
                                        onClick={link.onClick}
                                        className={clsx(
                                            "nav-link",
                                            link.roundedClass,
                                            "nav-link-inactive",
                                            "tw-cursor-pointer"
                                        )}
                                    >
                                        {link.label}
                                    </button>
                                ) : (
                                    <a 
                                        key={index}
                                        href={link.href} 
                                        className={clsx(
                                            "nav-link",
                                            link.roundedClass,
                                            "nav-link-inactive"
                                        )}
                                    >
                                        {link.label}
                                    </a>
                                )
                            )}
                        </div>
                    </nav>
                )}

                <div className={`tw-flex tw-justify-center tw-bg-transparent tw-mb-0 ${responsivePaddingClasses}`}>
                    {/* Calendar Card */}
                    <div className={calendarCardClasses}>
                        
                        {/* React Calendar */}
                        <div className="tw-w-full habit-calendar">
                            <ReactCalendar
                                value={currentDate}
                                onActiveStartDateChange={handleActiveStartDateChange}
                                minDate={new Date(new Date().getFullYear(), 0, 1)}
                                maxDate={new Date()} 
                                tileContent={tileContent}
                                tileClassName={tileClassName}
                                tileDisabled={() => true}
                                showNeighboringMonth={true}
                                className="tw-w-full tw-border-0"
                                navigationLabel={navigationLabel}
                                showNavigation={true}
                                prev2Label={null}
                                next2Label={null}
                                minDetail="month"
                                calendarType="hebrew"
                            />
                        </div>

                        {/* Legend */}
                        <div className={`tw-flex tw-justify-center tw-gap-6 tw-mt-4 tw-font-semibold ${isMobile ? 'tw-text-base tw-flex-wrap' : 'tw-text-xl'}`}>
                            <span className="tw-text-[#5690b1]">✓ Completed</span>
                            <span className="tw-text-red-500">✗ Missed</span>
                            <span className="tw-text-gray-700">Not attempted</span>
                            <span className="tw-text-[#000033]"><span className="tw-inline-block tw-w-3 tw-h-3 tw-rounded-full tw-bg-[#000033] tw-mr-1 tw-align-middle"></span> Sunday (Rest Day)</span>
                        </div>
                    </div>
                </div>

                {/* Stats Section */}
                <div className={`tw-w-full tw-flex tw-justify-center tw-mb-14 tw-mt-8 tw-pb-20 ${responsivePaddingClasses}`}>
                    <div className={cardContainerClasses}>
                        {(isMobile || isTabletPortrait) ? (
                            // Mobile Layout
                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-[18px] tw-px-2.5 tw-py-[18px] tw-pb-[46px]">
                                <div className={clsx(flexColCenterClasses, 'tw-text-center')}>
                                    <h2 className={clsx(ctaHeadingClasses, 'tw-max-w-[300px] tw-text-center')}>
                                        DON'T LOSE YOUR STREAK!{' '}<br />START TODAY'S WORKOUT
                                    </h2>
                                </div>
                                <div className={mobileWorkoutContainerClasses}>
                                    <div className={mobileWorkoutInnerClasses}>
                                        <h3 className={clsx(oswaldHeadingClasses, 'tw-mt-2 tw-mb-3 tw-text-left')}>
                                            Today's Workout
                                        </h3>
                                        <a 
                                            href="/?habit-tracker"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                sessionStorage.setItem('autoplayVideo', 'true');
                                                window.location.href = '/?habit-tracker';
                                            }}
                                            className="tw-block tw-w-full tw-cursor-pointer"
                                        >
                                            <img
                                                src={thumbnailUrl}
                                                alt="Workout"
                                                className="tw-w-full tw-h-auto tw-max-h-[250px] tw-object-contain tw-rounded-lg"
                                            />
                                        </a>
                                    </div>
                                </div>
                                <div className={statsContainerMobileClasses}>
                                    <StreakStats currentStreak={currentStreak} personalRecord={personalRecord} />
                                    <div className="tw-flex tw-justify-end tw-w-full">
                                        <a href="/?habit-tracker/badges" className={badgesLinkClasses}>
                                            CHECK YOUR BADGES
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Desktop Layout
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-0 tw-flex-row">
                                <div className="tw-flex-1 tw-flex tw-flex-col tw-justify-between tw-items-start tw-h-[403px] tw-py-[18px] tw-pb-[46px] tw-relative">
                                    <StreakStats 
                                        currentStreak={currentStreak} 
                                        personalRecord={personalRecord}
                                        className={`tw-flex tw-flex-col tw-items-stretch tw-gap-7 tw-rounded-xl tw-bg-white tw-mx-[18px] tw-px-4 tw-py-7 ${isTabletPortrait ? 'tw-w-[800px]' : 'tw-w-[477px]'}`}
                                    />
                                    <div className={clsx(flexColStartClasses, 'tw-mt-auto')}>
                                        <div className={clsx(flexColStartClasses, 'tw-ml-11 tw-text-left')}>
                                            <h2 className={clsx(ctaHeadingClasses, 'tw-text-left tw-h-auto tw-w-[359px]')}>
                                                DON'T LOSE YOUR STREAK!{' '}<br />START TODAY'S WORKOUT
                                            </h2>
                                            <button 
                                                onClick={() => {
                                                    sessionStorage.setItem('autoplayVideo', 'true');
                                                    window.location.href = '/?habit-tracker';
                                                }}
                                                className={`btn-action btn-action-lg ${isTabletPortrait ? 'tw-w-[500px]' : 'tw-w-[300px]'}`}
                                            >
                                                START WORKOUT
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className={flexColStartClasses}>
                                    <h3 className={clsx(oswaldHeadingClasses, 'tw-mb-3 tw-text-left tw-hidden')}>
                                        Today's Workout
                                    </h3>
                                    <WorkoutImage
                                        thumbnailUrl={thumbnailUrl}
                                        className={clsx(workoutImageClasses, 'tw-w-[717px] tw-h-[403px] tw-object-cover')}
                                        onClick={() => {
                                            sessionStorage.setItem('autoplayVideo', 'true');
                                            window.location.href = '/?habit-tracker';
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="modal-overlay tw-z-[1002]" onClick={handleLogoutCancel}>
                    <div className="modal-container tw-items-center tw-text-center" onClick={(e) => e.stopPropagation()}>
                        <button 
                            onClick={handleLogoutCancel}
                            className="tw-absolute tw-top-6 tw-right-6 tw-bg-transparent tw-border-0 tw-text-3xl tw-cursor-pointer tw-text-gray-400 hover:tw-text-black"
                        >
                            ×
                        </button>

                        <h1 className="modal-title tw-mb-8">
                            LOGOUT
                        </h1>
                        
                        <p className="modal-text tw-text-2xl tw-mb-8">
                            Are you sure you want to logout?
                        </p>
                        
                        <button 
                            onClick={handleLogoutConfirm}
                            className="btn-primary tw-mb-4"
                        >
                            YES, LOGOUT
                        </button>
                        
                        <button 
                            onClick={handleLogoutCancel}
                            className="btn-secondary"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            )}
            </div>
        </>
    );
}

export default Calendar;